import { isClearFlow } from "@common/utils/clear-flow";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Hides the Crisp support bubble throughout the chromeless "clear" flow.
 *
 * Two mechanisms, because either alone is insufficient:
 *  - a CSS rule keyed on the `data-clear-flow` attribute (globals-website.css), which
 *    useClearFlowBranding owns. Crisp injects asynchronously and re-renders itself, so
 *    CSS is what guarantees the bubble is never briefly visible, with no polling.
 *  - the Crisp API (`chat:hide`) actually closes the widget rather than just hiding it,
 *    so it cannot be opened underneath and does not come back on re-init.
 *
 * Both are reverted when leaving the flow, so the regular product keeps its support
 * chat. `window.$crisp` is a queue array before the SDK loads, so pushing early is
 * safe — but it is absent entirely if the pixel never injected, hence the guard.
 */
export function useClearFlowCrisp(): void {
  const location = useLocation();

  // Only THIS hook's own hide is ever undone. Other pages hide the bubble for
  // their own reasons (diagnostics-view does, on mount) and this effect runs
  // after theirs, so an unconditional chat:show would revive it there.
  const weHidIt = useRef(false);

  useEffect(() => {
    const hidden = isClearFlow(location.pathname);
    if (!hidden && !weHidIt.current) return;

    try {
      window.$crisp?.push(["do", hidden ? "chat:hide" : "chat:show"]);
      weHidIt.current = hidden;
    } catch {
      // Crisp is optional (blocked by an ad blocker, or not injected in dev) —
      // the CSS rule above already covers the visual side.
    }
  }, [location.pathname]);

}
